// frontend/src/features/agency/AgencyBannedDriverList.js
// Phase 8.7C: Agency Banned lane
//
// Key principles:
// - Membership is ban-table derived (banned_drivers), NOT driver.status.
// - We render from local `rows` (this page’s membership source of truth).
// - Canonical reads come from GET /agencies/me/drivers/banned.
// - This component does NOT subscribe to socket events anymore.
// - Real-time invalidation + stats bump are owned by services/socket.js.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { upsertDrivers } from '../drivers/driverSlice';
import { fetchAgencyBannedDrivers } from '../../services/driverService';

import { selectAgencyIdForUser } from '../auth/authSlice';
import { selectQuery, selectStatsRefreshKey } from '../ui/filtersSlice';

function filterRowsByQuery(rows, q) {
  const needle = (q ?? '').trim().toLowerCase();
  if (!needle) return rows || [];

  return (rows || []).filter((row) => {
    const haystacks = [
      row?.name,
      row?.vehicle_registration_number,
      row?.assignment_status,
      row?.assignment_id != null ? String(row.assignment_id) : null,
      row?.ban_reason,
      row?.reason,
      row?.ban_scope,
      row?.ban_site_id != null ? String(row.ban_site_id) : null,
      row?.ban_id != null ? String(row.ban_id) : null,
    ];

    return haystacks.some((v) => String(v ?? '').toLowerCase().includes(needle));
  });
}

export default function BannedDriverList() {
  const dispatch = useDispatch();
  const agencyId = useSelector(selectAgencyIdForUser);
  const statsKey = useSelector(selectStatsRefreshKey);
  const q = useSelector(selectQuery);

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  async function reload() {
    const list = await fetchAgencyBannedDrivers();
    setRows(list);
    dispatch(upsertDrivers(list)); // optional entity cache
  }

  useEffect(() => {
    if (!agencyId) return;

    (async () => {
      try {
        setError(null);
        await reload();
      } catch (err) {
        setError('Failed to load banned drivers.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [agencyId, statsKey]);

  const filtered = useMemo(() => filterRowsByQuery(rows, q), [rows, q]);
  const sorted = useMemo(() => [...filtered], [filtered]);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading…</div>;
  }

  return (
    <div className="my-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-2xl font-bold text-gray-800">Banned Drivers</h3>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-700 bg-red-100 p-3 rounded">
          {error}
        </div>
      )}

      {q?.trim() && sorted.length === 0 && (
        <div className="text-sm text-gray-500 py-3">
          No matches for “{q.trim()}”.
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50">
            <tr>
              {['Name', 'Vehicle Reg', 'Scope', 'Site', 'Reason', 'Banned At'].map((col) => (
                <th
                  key={col}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {sorted.map((row) => (
              <tr key={`${row.id}-${row.ban_id ?? 'ban'}`} className="hover:bg-gray-50">
                <td className="px-6 py-4">{row.name}</td>
                <td className="px-6 py-4">{row.vehicle_registration_number}</td>
                <td className="px-6 py-4">{row.ban_scope ?? '—'}</td>
                <td className="px-6 py-4">{row.ban_site_id ?? '—'}</td>
                <td className="px-6 py-4 text-gray-700">{row.ban_reason ?? row.reason ?? '—'}</td>
                <td className="px-6 py-4 text-gray-700">
                  {row.banned_at ? new Date(row.banned_at).toLocaleString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}